// Cab Calamity: app flow. identity -> name -> home -> (practice | host | join) -> lobby -> session -> results.

import { UniversalCamera } from '@babylonjs/core/Cameras/universalCamera';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { createEngine, type GameEngine } from './game/engine';
import { loadWorld, fetchLevel, CityLibrary, type World } from './game/world';
import type { LevelIndex, LevelInfo } from './levels/types';
import { parseFlags, serializeFlags, describeFlags, type Flags } from './sim/flags';
import { VehicleLibrary } from './game/vehicle';
import { PeopleLibrary } from './game/people';
import { DebugMenu } from './game/debug';
import { Session } from './game/session';
import { Sfx } from './game/audio';
import { LoadingScreen, setupFullscreenButton } from './ui/hud';
import { Screens, toast } from './ui/screens';
import { loadIdentity, recordResult, saveIdentity, type Identity } from './app/identity';
import { RoomClient, type RosterEntry } from './net/client';
import { BASE, ASSET_BASE } from './game/config';
import type { PlayerState } from './sim/match';

declare global { interface Window { cab: CabDebug; } }
export interface CabDebug {
  session: () => Session | null;
  car: () => import('./sim/car').CarState | null;
  setInput(steer: number, brake: number, nitro?: boolean, throttle?: number): void;
  teleport(x: number, z: number, yawDeg?: number | null): void;
  stats(): Record<string, unknown>;
  world: () => World;
  match: () => import('./sim/match').Match | null;
  practice(bots?: number, minutes?: number): void;
  host(): void;
  join(code: string): void;
  start(): void;
  addBot(): void;
  screen: () => string;
  level: () => string;
  flags: () => Flags;
  setFlags: (spec: string) => Flags;
  scene: () => unknown;
  setLevel: (id: string) => Promise<void>;
  report: () => string;
  devmenu: (open: boolean) => void;
  disaster(kind: 'tsunami' | 'earthquake' | 'volcano' | 'cyclone'): void;
  karma(v: number): void;
  streets(): Record<string, unknown> | null;
}

const BOT_ROSTER = ['Robo-Cab', 'Autopilot Al', 'Tin Lizzie', 'Cabbot', 'Meter Maid', 'Rusty', 'Nav-Man'];
const BOT_COLOURS = ['E5433A', '3E8BFF', '4CC26B', '9B5DE5', 'FF7A1A', 'FF6BB5', 'F5F1E8'];

class App {
  identity: Identity;
  g!: GameEngine; world!: World; vehicles!: VehicleLibrary; people: PeopleLibrary | null = null; screens!: Screens;
  city!: CityLibrary; levels!: LevelIndex; levelId = 'test';
  flags: Flags = parseFlags(new URLSearchParams(location.search).get('flags'));
  session: Session | null = null;
  net: RoomClient | null = null;
  roster: RosterEntry[] = []; roomCode = ''; isHost = false; lobbyStatus = ''; lobbyBots = 0;
  screenName = 'loading';
  private params = new URLSearchParams(location.search);
  private idleCam: UniversalCamera | null = null;
  private menuSfx = new Sfx();
  private devMenu: DebugMenu | null = null;

  constructor() { this.identity = loadIdentity(); }

  async boot(): Promise<void> {
    const canvas = document.getElementById('game') as HTMLCanvasElement;
    const ui = document.getElementById('ui')!;
    setupFullscreenButton(document.getElementById('fullscreen')!);
    const loading = new LoadingScreen(ui);
    this.g = createEngine(canvas);
    loading.set('Reading the map', 0.1);
    this.levels = await fetch(`${ASSET_BASE}levels/index.json`).then((r) => r.json() as Promise<LevelIndex>);
    const wanted = this.params.get('level') ?? (() => { try { return localStorage.getItem('cab.level'); } catch { return null; } })();
    this.levelId = this.levels.levels.some((l) => l.id === wanted) ? wanted! : this.levels.default;
    this.city = await CityLibrary.load(this.g.scene, this.g.shadows);
    this.world = await loadWorld(this.g.scene, this.city, await fetchLevel(this.levelId, this.level.v), (l) => loading.set(l, 0.5));
    loading.set('Polishing the taxis', 0.85);
    this.vehicles = await VehicleLibrary.load(this.g.scene);
    loading.set('Waking up the townsfolk', 0.92);
    try { this.people = await PeopleLibrary.load(this.g.scene); } catch (e) { console.warn('[cab] people pack not loaded', e); }
    this.screens = new Screens(ui);
    this.devMenu = new DebugMenu(this.g.scene, this.world, () => this.session, ui);
    loading.set('Ready', 1); setTimeout(() => loading.remove(), 200);
    window.cab = this.debugApi();
    this.setupIdleCamera();
    this.g.engine.runRenderLoop(this.idleRender);

    const auto = this.params.get('auto');
    if (auto === 'practice') { this.identity.name ||= 'Tester'; return this.startPractice(Number(this.params.get('bots') ?? 3), Number(this.params.get('minutes') ?? 10)); }
    if (this.identity.name) this.showHome(); else this.showName();
  }

  private idleRender = () => { this.g.scene.render(); };
  get level(): LevelInfo { return this.levels.levels.find((l) => l.id === this.levelId) ?? this.levels.levels[0]; }
  /** Guest side of a level change: load it without re-broadcasting. */
  async setLevelQuiet(id: string): Promise<void> {
    if (id === this.levelId || !this.levels.levels.some((l) => l.id === id)) return;
    this.levelId = id; this.world.dispose(); this.world = await loadWorld(this.g.scene, this.city, await fetchLevel(id, this.level.v));
    this.devMenu?.dispose(); this.devMenu = new DebugMenu(this.g.scene, this.world, () => this.session, document.getElementById('ui')!); this.setupIdleCamera();
    if (this.screenName === 'lobby') this.showLobby();
  }
  /** Swap the loaded level (menus only, never mid-match). */
  async setLevel(id: string): Promise<void> {
    if (id === this.levelId || !this.levels.levels.some((l) => l.id === id)) return;
    this.levelId = id; try { localStorage.setItem('cab.level', id); } catch { /* ignore */ }
    this.world.dispose();
    this.world = await loadWorld(this.g.scene, this.city, await fetchLevel(id, this.level.v));
    this.devMenu?.dispose(); this.devMenu = new DebugMenu(this.g.scene, this.world, () => this.session, document.getElementById('ui')!);
    this.setupIdleCamera();
    this.net?.sendTo(null, { t: 'level', id }, true);
  }
  /** A slow orbit high above downtown, shown behind the menus. */
  private setupIdleCamera(): void {
    this.idleCam?.dispose(); this.idleCam = null;
    const rank = this.world.data.taxi_ranks[1] ?? this.world.data.taxi_ranks[0];
    const cx = rank?.p[0] ?? 0, cz = rank?.p[2] ?? 0;
    const c = new UniversalCamera('idle', new Vector3(cx - 60, 70, cz - 60), this.g.scene);
    c.setTarget(new Vector3(cx, 10, cz)); c.fov = 0.9; c.maxZ = 1500; c.inputs.clear();
    this.idleCam = c;
    let t = 0;
    this.g.scene.onBeforeRenderObservable.add(() => {
      if (this.session || this.g.scene.activeCamera !== c) return;
      t += this.g.engine.getDeltaTime() / 1000;
      c.position.set(cx + Math.sin(t * 0.05) * 90, 70, cz + Math.cos(t * 0.05) * 90); c.setTarget(new Vector3(cx, 8, cz));
    });
    this.g.scene.activeCamera = c;
  }

  // ------------------------------------------------------------------ screens
  showName(): void {
    this.screenName = 'name';
    this.screens.name(this.identity, (name, colour) => { this.identity.name = name; this.identity.colour = colour; saveIdentity(this.identity); this.net?.updateSelf(name, colour); this.showHome(); });
  }
  showHome(): void {
    this.screenName = 'home'; this.menuSfx.playMusic('menu', 0.3);
    this.screens.home(this.identity, { host: () => this.hostRoom(), join: (code) => this.joinRoom(code), practice: () => this.startPractice(3, 10), edit: () => this.showName() }, { levels: this.levels.levels, current: this.levelId, pick: (id) => { void this.setLevel(id).then(() => this.showHome()); } });
  }

  private wsUrl(): string { const proto = location.protocol === 'https:' ? 'wss' : 'ws'; return `${proto}://${location.host}${BASE}ws`; }
  private async connect(): Promise<RoomClient> {
    if (this.net) return this.net;
    const net = new RoomClient(this.wsUrl());
    await net.connect();
    net.onError = (e) => { this.lobbyStatus = e; if (this.screenName === 'lobby') this.showLobby(); else toast(e); };
    net.onClose = () => { if (this.screenName === 'lobby') { toast('Lost the server connection'); this.leaveRoom(); } };
    net.onRoster = (roster, hostId) => { this.roster = roster; this.isHost = hostId === net.id; if (this.isHost && this.lobbyBots) net.sendTo(null, { t: 'lobbybots', n: this.lobbyBots }, true); if (this.screenName === 'lobby') this.showLobby(); };
    net.onStart = (seed, _at, level, flags) => { void (async () => { if (flags) this.flags = parseFlags(flags); if (level && level !== this.levelId) await this.setLevel(level); await this.startMultiplayer(seed); })(); };
    net.onMessage((_from, msg) => { if (msg.t === 'lobbybots') { this.lobbyBots = Number(msg.n); if (this.screenName === 'lobby') this.showLobby(); } if (msg.t === 'level' && !this.isHost) { void this.setLevelQuiet(String(msg.id)); } });
    this.net = net; return net;
  }
  async hostRoom(): Promise<void> {
    try { const net = await this.connect(); const w = await net.host(this.identity.name, this.identity.colour); this.roomCode = w.room; this.roster = w.players; this.isHost = true; this.showLobby(); }
    catch (e) { toast(String((e as Error).message)); }
  }
  async joinRoom(code: string): Promise<void> {
    try { const net = await this.connect(); const w = await net.join(code, this.identity.name, this.identity.colour); this.roomCode = w.room; this.roster = w.players; this.isHost = w.host === net.id; this.showLobby(); }
    catch (e) { toast(String((e as Error).message)); }
  }
  private botRoster(): RosterEntry[] { return Array.from({ length: this.lobbyBots }, (_, i) => ({ id: `bot${i}`, name: BOT_ROSTER[i], colour: BOT_COLOURS[i], kind: 'bot' as const, host: false })); }
  showLobby(): void {
    this.screenName = 'lobby';
    const url = `${location.origin}${BASE}?room=${this.roomCode}`;
    this.screens.lobby({ code: this.roomCode, roster: [...this.roster, ...this.botRoster()], me: this.net?.id ?? '', isHost: this.isHost, url, status: `${this.lobbyStatus ? this.lobbyStatus + ' · ' : ''}Features: ${describeFlags(this.flags)}`, levels: this.levels.levels, level: this.levelId }, {
      start: () => this.net?.start(Math.floor(Math.random() * 1e9), this.levelId, serializeFlags(this.flags)),
      pickLevel: (id) => { void this.setLevel(id).then(() => this.showLobby()); },
      addBot: () => this.addLobbyBot(),
      leave: () => this.leaveRoom(),
    });
  }
  private addLobbyBot(): void {
    if (this.lobbyBots >= 7 || this.roster.length + this.lobbyBots >= 8) return;
    this.lobbyBots++;
    this.net?.sendTo(null, { t: 'lobbybots', n: this.lobbyBots }, true);
    this.showLobby();
  }
  leaveRoom(): void { this.net?.close(); this.net = null; this.roster = []; this.roomCode = ''; this.lobbyStatus = ''; this.lobbyBots = 0; this.showHome(); }

  // ------------------------------------------------------------------ sessions
  private async ensureControls(): Promise<void> {
    const isTouch = matchMedia('(pointer: coarse)').matches;
    if (!isTouch || sessionStorage.getItem('cab.controls')) return;
    await new Promise<void>((resolve) => this.screens.permission({ allow: () => { sessionStorage.setItem('cab.controls', 'tilt'); resolve(); }, keyboard: () => { sessionStorage.setItem('cab.controls', 'keys'); resolve(); } }));
  }

  async startPractice(bots = 3, minutes = 10): Promise<void> {
    await this.ensureControls();
    this.beginSession({ mode: 'solo', seed: Math.floor(Math.random() * 1e9), durationSec: minutes * 60, localId: 'me', roster: [{ id: 'me', name: this.identity.name || 'You', colour: this.identity.colour, kind: 'human', host: true }], bots });
  }

  private async startMultiplayer(seed: number): Promise<void> {
    if (!this.net) return;
    await this.ensureControls();
    this.beginSession({ mode: this.isHost ? 'host' : 'client', seed, durationSec: 600, localId: this.net.id, roster: [...this.roster, ...this.botRoster()], bots: 0, net: this.net });
  }

  private beginSession(o: { mode: 'solo' | 'host' | 'client'; seed: number; durationSec: number; localId: string; roster: RosterEntry[]; bots: number; net?: RoomClient }): void {
    this.screenName = 'game'; this.menuSfx.stopMusic();
    this.g.engine.stopRenderLoop(this.idleRender);
    this.session?.dispose();
    this.session = new Session(this.g, this.world, this.vehicles, this.people, this.screens, { ...o, flags: this.flags, onEnd: (players) => this.showResults(players) });
    this.session.debug = this.devMenu;
    if (sessionStorage.getItem('cab.controls') === 'tilt') this.session.input.enableTilt().then((ok) => { if (!ok) toast('No tilt sensor found, using touch and keys'); });
  }

  private showResults(players: PlayerState[]): void {
    const me = players.find((p) => p.id === this.session?.opts.localId);
    if (me) recordResult(this.identity, { money: me.money, won: players[0]?.id === me.id, karmaMin: me.stats.karmaMin, karmaMax: me.stats.karmaMax });
    const menace = [...players].sort((a, b) => a.karma - b.karma)[0]; const employee = [...players].sort((a, b) => b.karma - a.karma)[0];
    const wasSolo = this.session?.opts.mode === 'solo';
    this.session?.dispose(); this.session = null;
    if (this.idleCam) this.g.scene.activeCamera = this.idleCam;
    this.g.engine.runRenderLoop(this.idleRender);
    this.screenName = 'results'; this.menuSfx.playMusic('results', 0.35); this.menuSfx.play('win');
    this.screens.results(players, me?.id ?? '', { menace: `${menace?.name ?? '-'} · ${menace?.stats.pedestrians ?? 0} pedestrians hit`, employee: `${employee?.name ?? '-'} · ${employee?.stats.redLightsObeyed ?? 0} red lights obeyed` }, {
      again: () => { if (wasSolo) void this.startPractice(3, 10); else if (this.net) this.showLobby(); else this.showHome(); },
      home: () => { if (this.net) this.leaveRoom(); else this.showHome(); },
    });
  }

  // ------------------------------------------------------------------ automation surface (window.cab)
  private debugApi(): CabDebug {
    return {
      session: () => this.session,
      car: () => this.session?.me.car ?? null,
      setInput: (steer, brake, nitro = false, throttle = 1) => this.session?.setForcedInput({ steer, brake, nitro, throttle }),
      teleport: (x, z, yawDeg) => { const s = this.session; if (!s) return; const c = s.me.car; c.x = x; c.z = z; c.y = this.world.ground.heightAt(x, z); c.yaw = yawDeg == null ? this.world.ground.roadHeading(x, z) : yawDeg * Math.PI / 180; c.vx = c.vz = 0; s.cam.snapTo(c); },
      stats: () => ({ fps: Math.round(this.g.engine.getFps()), active: this.g.scene.getActiveMeshes().length, meshes: this.g.scene.meshes.length, instances: this.world.instances.length, missing: this.world.missing, screen: this.screenName, players: this.session ? [...this.session.match.players.values()].map((p) => ({ id: p.id, name: p.name, kind: p.kind, money: p.money, hp: Math.round(p.health), fares: p.stats.fares, riding: p.riding.length, x: +p.car.x.toFixed(0), z: +p.car.z.toFixed(0) })) : [], timeLeft: this.session?.match.timeLeft() ?? null, phase: this.session?.match.phase ?? null, net: this.net ? { id: this.net.id, room: this.net.room, host: this.net.hostId, peers: this.net.peerStatus(), stats: this.net.stats } : null }),
      world: () => this.world,
      match: () => this.session?.match ?? null,
      practice: (bots = 3, minutes = 10) => { void this.startPractice(bots, minutes); },
      host: () => { void this.hostRoom(); }, join: (code) => { void this.joinRoom(code); }, start: () => this.net?.start(Math.floor(Math.random() * 1e9)), addBot: () => this.addLobbyBot(),
      screen: () => this.screenName,
      level: () => this.levelId,
      flags: () => this.flags,
      setFlags: (spec: string) => { this.flags = parseFlags(spec, this.flags); return this.flags; },
      scene: () => this.g.scene,
      setLevel: (id: string) => this.setLevel(id),
      report: () => this.devMenu?.report() ?? 'no menu',
      devmenu: (open) => this.devMenu?.setOpen(open),
      disaster: (kind) => this.session?.forceDisaster(kind),
      karma: (v) => { if (this.session) this.session.me.karma = v; },
      streets: () => this.session ? { peds: this.session.streets.peds.length, traffic: this.session.streets.traffic.length, police: this.session.streets.police.length, intersections: this.session.streets.intersections.length, disasters: this.session.director.active.map((a) => ({ kind: a.spec.kind, start: a.spec.start, impactors: a.impactors.length })), karma: this.session.me.karma, hp: Math.round(this.session.me.health) } : null,
    };
  }
}

new App().boot().catch((e) => { console.error(e); document.body.insertAdjacentHTML('beforeend', `<pre style="position:fixed;inset:auto 0 0 0;background:#E5433A;color:#fff;padding:12px;margin:0;z-index:99">${String(e?.stack ?? e)}</pre>`); });
